"use client";

import { useCallback, useEffect, useState } from "react";
import { getCoursesApi } from "../services/learning.api";
import type {
  LearningCourseItem,
  LearningCoursesQuery,
  LearningCoursesResponse,
} from "../types/learning";

type CoursesFetcher = (
  locale: string,
  query?: LearningCoursesQuery
) => Promise<LearningCoursesResponse>;

export function useCourses(
  locale: string,
  query: LearningCoursesQuery = {},
  fetchCourses: CoursesFetcher = getCoursesApi
) {
  const [courses, setCourses] = useState<LearningCourseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [meta, setMeta] = useState<LearningCoursesResponse["meta"] | null>(null);

  const loadPage = useCallback(
    async (page: number, append: boolean) => {
      const response = await fetchCourses(locale, {
        categorySlug: query.categorySlug,
        subCategorySlug: query.subCategorySlug,
        perPage: query.perPage,
        page,
        regionSlug: query.regionSlug,
      });

      setCourses((currentCourses) =>
        append ? [...currentCourses, ...response.data] : response.data
      );
      setMeta(response.meta);
    },
    [fetchCourses, locale, query.categorySlug, query.subCategorySlug, query.perPage, query.regionSlug]
  );

  useEffect(() => {
    let ignore = false;

    const loadInitialCourses = async () => {
      setLoading(true);

      try {
        const response = await fetchCourses(locale, {
          categorySlug: query.categorySlug,
          subCategorySlug: query.subCategorySlug,
          perPage: query.perPage,
          page: 1,
          regionSlug: query.regionSlug,
        });

        if (!ignore) {
          setCourses(response.data);
          setMeta(response.meta);
        }
      } catch {
        if (!ignore) {
          setCourses([]);
          setMeta(null);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    void loadInitialCourses();

    return () => {
      ignore = true;
    };
  }, [fetchCourses, locale, query.categorySlug, query.subCategorySlug, query.perPage, query.regionSlug]);

  const loadMore = useCallback(async () => {
    if (!meta?.hasMore || loadingMore) {
      return;
    }

    setLoadingMore(true);

    try {
      await loadPage(meta.currentPage + 1, true);
    } finally {
      setLoadingMore(false);
    }
  }, [loadPage, loadingMore, meta]);

  return {
    courses,
    loading,
    loadingMore,
    hasMore: meta?.hasMore ?? false,
    loadMore,
  };
}
